/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportModel } from 'src/app/reports/model/report-model';
import { DateService } from 'src/app/services/date.service';
import { environment } from 'src/environments/environment';

import { IGeneralReportData } from '../interfaces/general-report-data';
import {
  InsightsReport,
  InsightsReportToken,
  InsightsVendor,
} from '../interfaces/insights-reports';
import { GeneralReportModel } from '../models/general-report-model';
import { GeneralReportPerformersModel } from '../models/general-report-performers-model';

@Injectable({
  providedIn: 'root',
})
export class GeneralReportService {
  constructor(private http: HttpClient) {}

  public getInsightsVendors(): Observable<InsightsVendor[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.get<InsightsVendor[]>(
      environment.apiUrl + 'insights/vendors',
      {
        headers,
        withCredentials: true,
      }
    );
  }

  public getInsightsReports(): Observable<InsightsReport[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.get<InsightsReport[]>(
      environment.apiUrl + 'insights/reports',
      {
        headers,
        withCredentials: true,
      }
    );
  }

  public getInsightsVendorReports(
    vendorId: number
  ): Observable<InsightsReport[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.get<InsightsReport[]>(
      environment.apiUrl + `insights/vendors/${vendorId}/reports`,
      {
        headers,
        withCredentials: true,
      }
    );
  }

  public getInsightsReportToken(
    reportId: string,
    workspaceId: string,
    vendorId: number
  ): Observable<InsightsReportToken> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.get<InsightsReportToken>(
      `${environment.apiUrl}insights/reports/${reportId}/workspace/${workspaceId}/vendor/${vendorId}/token`,
      {
        headers,
        withCredentials: true,
      }
    );
  }

  public getPerformers(
    model: GeneralReportPerformersModel
  ): Observable<string[]> {
    const params = this.createReportPerformersRequestParams(model);
    return this.http.get<string[]>(
      environment.apiUrl + 'reports/general/performers',
      { params }
    );
  }

  public getGeneralReport(
    model: GeneralReportModel
  ): Observable<IGeneralReportData> {
    const params = this.createReportRequestParams(model);
    return this.http
      .get<IGeneralReportData>(environment.apiUrl + 'reports/general', {
        params,
      })
      .pipe(
        map((result) => {
          if (result && result.eventOverview) {
            result.eventOverview.dataType = 'event-overview';
          }
          return result;
        })
      );
  }

  public exportXlsx(model: GeneralReportModel): Observable<Blob> {
    const params = this.createReportRequestParams(model);
    return this.http.get<Blob>(environment.apiUrl + 'reports/general/export', {
      params,
      responseType: 'blob' as 'json',
    });
  }

  private createBaseReportRequestParams(model: ReportModel): HttpParams {
    let httpParams = new HttpParams();

    if (model.vendorIds) {
      httpParams = httpParams.append('vendorIds', model.vendorIds.join(','));
    }

    if (model.from) {
      httpParams = httpParams.append(
        'from',
        DateService.toStartOfDayString(model.from)
      );
    }

    if (model.to) {
      httpParams = httpParams.append(
        'to',
        DateService.toEndOfDayString(model.to)
      );
    }

    return httpParams;
  }

  private createReportPerformersRequestParams(
    model: GeneralReportPerformersModel
  ) {
    let params = this.createBaseReportRequestParams(model);

    if (model.eventType) {
      params = params.append('eventType', model.eventType);
    }

    return params;
  }

  private createReportRequestParams(model: GeneralReportModel) {
    let params = this.createReportPerformersRequestParams(model);

    if (model.performer) {
      params = params.append('performer', model.performer);
    }

    return params;
  }
}
